import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { supabase } from "./supabase/client"
import { logger, obfuscateUrl } from './logger'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Cache de URLs assinadas
type SignedUrlCache = {
  [key: string]: {
    url: string
    expiresAt: number
  }
}

// Configurações de cache
const CACHE_KEY = 'signed_url_cache'
const CACHE_BUFFER = 300 // 5 minutos em segundos
const DEFAULT_EXPIRY = 7200 // 2 horas em segundos

// Carregar cache do localStorage
let urlCache: SignedUrlCache = {}
let lastCacheSave = 0 // Para throttling do cache no iOS

try {
  const savedCache = localStorage.getItem(CACHE_KEY)
  if (savedCache) {
    const parsed = JSON.parse(savedCache)
    const now = Math.floor(Date.now() / 1000)
    
    // Filtrar apenas URLs não expiradas
    Object.keys(parsed).forEach(key => {
      if (parsed[key].expiresAt > now) {
        urlCache[key] = parsed[key]
      }
    })
  }
} catch (error) {
  console.error('Erro ao carregar cache:', error)
}

// Função para salvar cache no localStorage
function saveCache() {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(urlCache))
  } catch (error) {
    console.error('Erro ao salvar cache:', error)
  }
}

// Função para normalizar o caminho da imagem
function normalizePath(path: string): string {
  try {
    // Se já é uma URL completa do Supabase, retornar como está
    if (path.includes('storage.supabase.co')) {
      return path
    }

    // Decodifica a URL para tratar espaços e caracteres especiais
    const decodedPath = decodeURIComponent(path)

    // Remove a barra inicial se existir
    const cleanPath = decodedPath.startsWith('/') ? decodedPath.slice(1) : decodedPath

    // Se começa com public_assets, retornar como está
    if (cleanPath.startsWith('public_assets/')) {
      return cleanPath
    }

    // Se começa com ui/ ou series/, é um asset público
    if (cleanPath.startsWith('ui/') || cleanPath.startsWith('series/')) {
      return `public_assets/${cleanPath}`
    }

    // Se começa com cards/, remover o prefixo
    if (cleanPath.startsWith('cards/')) {
      return cleanPath.slice(6)
    }

    // Se não tem prefixo conhecido, retornar como está
    return cleanPath
  } catch (error) {
    console.error('Erro ao normalizar caminho:', error)
    return path
  }
}

/**
 * Obtém uma URL assinada do Supabase Storage ou URL pública
 * @param path Caminho do arquivo no bucket
 * @param expiresIn Tempo de expiração em segundos (apenas para URLs assinadas)
 * @returns URL assinada/pública ou null em caso de erro
 */
export async function getSignedUrl(path: string, expiresIn: number = DEFAULT_EXPIRY) {
  try {
    const now = Math.floor(Date.now() / 1000)
    const normalizedPath = normalizePath(path)
    
    // Verificar cache usando o caminho normalizado
    const cached = urlCache[normalizedPath]
    if (cached && cached.expiresAt - CACHE_BUFFER > now) {
      return cached.url
    }

    const startTime = Date.now()
    
    let publicUrl: string

    // Se o caminho começa com 'public_assets/', retorna a URL pública
    if (normalizedPath.startsWith('public_assets/')) {
      const filePath = normalizedPath.replace('public_assets/', '')
      logger.sensitive('Gerando URL pública para:', obfuscateUrl(filePath))
      const { data } = supabase
        .storage
        .from('public_assets')
        .getPublicUrl(filePath)
      publicUrl = data.publicUrl
    }
    // Para URLs completas do Supabase Storage, retorna a URL diretamente
    else if (normalizedPath.includes('storage.supabase.co')) {
      publicUrl = normalizedPath
    }
    // Para outros caminhos, assume que é um asset do bucket 'cards'
    else {
      logger.sensitive('Gerando URL pública para caminho padrão:', obfuscateUrl(normalizedPath))
      const { data } = supabase
        .storage
        .from('cards')
        .getPublicUrl(normalizedPath)
      publicUrl = data.publicUrl
    }

    // Armazenar no cache
    urlCache[normalizedPath] = {
      url: publicUrl,
      expiresAt: now + expiresIn
    }

    // Salvar no localStorage (com throttling para iOS)
    if (isIOS()) {
      // Salvar cache menos frequentemente no iOS para evitar bloqueios
      const timeSinceLastSave = now - (lastCacheSave || 0)
      if (timeSinceLastSave > 5) { // Salvar no máximo a cada 5 segundos
        saveCache()
        lastCacheSave = now
      }
    } else {
      saveCache()
    }

    const loadTime = Date.now() - startTime
    logger.sensitive(`URL pública gerada em ${loadTime}ms:`, obfuscateUrl(publicUrl))
    return publicUrl
    
  } catch (error) {
    console.error('Erro ao gerar URL:', error)
    // Se falhar, tentar usar a URL original se for uma URL completa
    if (path.startsWith('http')) {
      return path
    }
    return null
  }
}

/**
 * Obtém URLs assinadas para múltiplos arquivos
 * @param paths Array de caminhos dos arquivos
 * @param expiresIn Tempo de expiração em segundos
 * @returns Array de URLs assinadas
 */
export async function preloadSignedUrls(paths: string[], expiresIn = DEFAULT_EXPIRY) {
  // Filtrar apenas os caminhos que não estão em cache ou estão próximos de expirar
  const now = Math.floor(Date.now() / 1000)
  const pathsToLoad = paths.filter(path => {
    const normalizedPath = normalizePath(path)
    const cached = urlCache[normalizedPath]
    return !cached || cached.expiresAt - CACHE_BUFFER <= now
  })

  // Carregar em paralelo, mas com limite de 5 requisições simultâneas
  const results = []
  for (let i = 0; i < pathsToLoad.length; i += 5) {
    const batch = pathsToLoad.slice(i, i + 5)
    const batchResults = await Promise.all(batch.map(path => getSignedUrl(path, expiresIn)))
    results.push(...batchResults)
  }

  return results
}

/**
 * Processa URLs de imagens do Supabase Storage
 * Funciona para séries, cartas, badges, avatares, etc.
 * @param imageUrl URL parcial ou completa da imagem
 * @returns URL completa processada ou null
 */
export function getImageUrl(imageUrl: string): string | null {
  try {
    if (!imageUrl) return null

    // Se já é uma URL completa do Supabase, usar diretamente
    if (imageUrl.includes('supabase.co/storage/v1/object/public/')) {
      return imageUrl
    }

    // Se é um caminho relativo, assumir que é do bucket public_assets
    if (!imageUrl.startsWith('http')) {
      return `https://fqgglbhawczmdkkhhtnz.supabase.co/storage/v1/object/public/public_assets/${imageUrl}`
    }

    // Fallback: retornar a URL original
    return imageUrl
  } catch (error) {
    console.error('Erro ao processar URL da imagem:', error)
    return null
  }
}

/**
 * @deprecated Use getImageUrl() para funcionalidade genérica
 * Mantido para compatibilidade com código existente
 */
export function getSeriesImageUrl(imageUrl: string): string | null {
  return getImageUrl(imageUrl)
}

/**
 * Ajusta a URL da carta baseado na raridade
 * Substitui /Common/ ou /common/ pela raridade correta, mantendo o case original
 */
export function getCardImageUrlByRarity(
  baseUrl: string,
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'
): string {
  try {
    if (!baseUrl || !rarity) return baseUrl

    // Detectar o case usado na URL original (maiúscula ou minúscula)
    const match = baseUrl.match(/\/(common|uncommon|rare|epic|legendary|Common|Uncommon|Rare|Epic|Legendary)\//i)
    
    if (!match) {
      console.warn('⚠️  URL não contém pasta de raridade:', baseUrl)
      return baseUrl
    }

    const originalRarity = match[1]
    const isUpperCase = originalRarity[0] === originalRarity[0].toUpperCase()

    // Mapear raridades mantendo o case detectado
    type RarityMap = Record<'common' | 'uncommon' | 'rare' | 'epic' | 'legendary', string>
    
    const rarityFolders: RarityMap = isUpperCase ? {
      'common': 'Common',
      'uncommon': 'Uncommon',
      'rare': 'Rare',
      'epic': 'Epic',
      'legendary': 'Legendary'
    } : {
      'common': 'common',
      'uncommon': 'uncommon',
      'rare': 'rare',
      'epic': 'epic',
      'legendary': 'legendary'
    }

    const targetFolder = rarityFolders[rarity.toLowerCase() as keyof RarityMap]
    
    // Substituir a raridade mantendo o case
    const urlWithCorrectRarity = baseUrl.replace(
      /\/(common|uncommon|rare|epic|legendary|Common|Uncommon|Rare|Epic|Legendary)\//i,
      `/${targetFolder}/`
    )

    // Log apenas se houver mudança e não for common
    if (targetFolder.toLowerCase() !== originalRarity.toLowerCase() && rarity !== 'common') {
      console.log(`🎨 Ajustando URL: /${originalRarity}/ → /${targetFolder}/`)
    }

    return urlWithCorrectRarity
  } catch (error) {
    console.error('Erro ao ajustar URL da carta por raridade:', error)
    return baseUrl
  }
}

/**
 * Detecta se está rodando no iOS
 */
function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

/**
 * Pré-carrega imagens na memória do navegador - otimizado para iOS
 * @param urls Array de URLs das imagens
 * @returns Promise que resolve quando todas as imagens forem carregadas
 */
export function preloadImages(urls: (string | null)[]) {
  const validUrls = urls.filter((url): url is string => Boolean(url))
  
  // No iOS, limitar preload simultâneo para evitar sobrecarga
  if (isIOS()) {
    return preloadImagesSequentially(validUrls, 3) // Máximo 3 simultâneas
  }
  
  return Promise.all(
    validUrls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = resolve
        img.onerror = reject
        img.src = url
      })
    })
  )
}

/**
 * Pré-carrega imagens em lotes para iOS
 */
async function preloadImagesSequentially(urls: string[], batchSize: number = 3): Promise<void[]> {
  const results: void[] = []
  
  for (let i = 0; i < urls.length; i += batchSize) {
    const batch = urls.slice(i, i + batchSize)
    const batchPromises = batch.map(url => {
      return loadImageWithRetry(url, 2) // Usar função otimizada com retry
        .then(() => {}) // Converter para void
        .catch((error) => {
          console.warn('Falha ao carregar imagem:', url, error.message)
          // Não falhar o batch inteiro
        })
    })
    
    const batchResults = await Promise.all(batchPromises)
    results.push(...batchResults)
    
    // Pausa entre lotes no iOS para evitar sobrecarga
    if (i + batchSize < urls.length) {
      await new Promise(resolve => setTimeout(resolve, isIOS() ? 200 : 50))
    }
  }
  
  return results
}

/**
 * Limpa URLs expiradas do cache
 */
export function cleanUrlCache() {
  const now = Math.floor(Date.now() / 1000)
  let changed = false

  Object.keys(urlCache).forEach(key => {
    if (urlCache[key].expiresAt <= now) {
      delete urlCache[key]
      changed = true
    }
  })

  if (changed) {
    saveCache()
  }
}

/**
 * Gera URL de imagem modificando a pasta de raridade no caminho
 * @param originalUrl URL original da imagem
 * @param currentRarity Raridade atual da carta
 * @returns URL modificada com pasta de raridade ou URL original se comum
 */
export function getUpgradedImageUrl(originalUrl: string, currentRarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'): string {
  // Se for comum, retornar a URL original
  if (currentRarity === 'common') {
    return originalUrl
  }

  try {
    // Mapeamento de raridades para pastas
    const rarityFolders = {
      uncommon: 'Uncommon',
      rare: 'Rare',
      epic: 'Epic', 
      legendary: 'Legendary'
    } as const

    const targetFolder = rarityFolders[currentRarity as keyof typeof rarityFolders]
    
    if (!targetFolder) {
      console.warn('Raridade não reconhecida:', currentRarity)
      return originalUrl
    }

    // Verificar se a URL tem o padrão esperado com pasta de raridade
    // Exemplo: /series/JoJo's_Bizarre_Adventure_1/Common//0012.png ou 0012.webp
    const rarityFolderPattern = /(\/series\/[^\/]+\/)([^\/]+)(\/\/[^\/]+\.(png|jpg|jpeg|webp))$/i
    const match = originalUrl.match(rarityFolderPattern)
    
    if (match) {
      const [, seriesPath, , filePath] = match
      
      // Substituir a pasta atual pela pasta da nova raridade
      const upgradedUrl = `${seriesPath}${targetFolder}${filePath}`
      
      console.log(`URL de carta atualizada para ${currentRarity}:`, originalUrl, '->', upgradedUrl)
      
      return upgradedUrl
    } else {
      // Se não encontrou o padrão esperado, tentar um padrão mais flexível
      // Buscar qualquer pasta de raridade conhecida no caminho
      const knownRarities = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary']
      let upgradedUrl = originalUrl
      
      for (const rarity of knownRarities) {
        if (originalUrl.includes(`/${rarity}/`)) {
          upgradedUrl = originalUrl.replace(`/${rarity}/`, `/${targetFolder}/`)
          console.log(`URL de carta atualizada para ${currentRarity} (padrão flexível):`, originalUrl, '->', upgradedUrl)
          return upgradedUrl
        }
      }
      
      console.warn('Não foi possível identificar pasta de raridade na URL:', originalUrl)
      return originalUrl
    }
  } catch (error) {
    console.error('Erro ao gerar URL de carta upgrade:', error)
    return originalUrl
  }
}

/**
 * Função específica para iOS: carrega imagem com retry e timeout otimizado
 */
export function loadImageWithRetry(url: string, maxRetries: number = 3): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    let attempts = 0
    
    function attemptLoad() {
      attempts++
      const img = new Image()
      const timeoutMs = isIOS() ? 15000 : 10000 // Timeout mais generoso para iOS
      
      const timeout = setTimeout(() => {
        img.src = '' // Cancelar carregamento
        if (attempts < maxRetries) {
          console.warn(`Tentativa ${attempts} falhou para ${url}, tentando novamente...`)
          setTimeout(attemptLoad, 1000 * attempts) // Backoff progressivo
        } else {
          reject(new Error(`Falha ao carregar imagem após ${maxRetries} tentativas: ${url}`))
        }
      }, timeoutMs)
      
      img.onload = () => {
        clearTimeout(timeout)
        resolve(img)
      }
      
      img.onerror = () => {
        clearTimeout(timeout)
        if (attempts < maxRetries) {
          console.warn(`Erro na tentativa ${attempts} para ${url}, tentando novamente...`)
          setTimeout(attemptLoad, 1000 * attempts) // Backoff progressivo
        } else {
          reject(new Error(`Erro ao carregar imagem após ${maxRetries} tentativas: ${url}`))
        }
      }
      
      // Configurações específicas para iOS
      if (isIOS()) {
        img.crossOrigin = 'anonymous'
        img.decoding = 'async'
      }
      
      img.src = url
    }
    
    attemptLoad()
  })
} 